<template>
    <div class="pb2-palette">
        <div class="pb2-palette__search-wrap">
            <input v-model="query" type="text" class="pb2-palette__search" :placeholder="t('search_blocks')" />
            <kbd class="pb2-palette__search-kbd" aria-hidden="true">⌘K</kbd>
        </div>

        <HintChip v-if="cmdkHint.visible.value" :text="t('hint_palette_cmdk', { mod })" @dismiss="cmdkHint.dismiss()" />

        <p v-if="!groups.length" class="pb2-palette__empty">
            {{ t('palette_empty') }}
        </p>

        <div v-for="group in groups" :key="group.key" class="pb2-palette__group">
            <h4 class="pb2-palette__group-title">{{ group.title }}</h4>

            <!-- Typy bloků: decentní dlaždicová mřížka s ikonami (F4, vzor v1) -->
            <ul v-if="group.tiles" class="pb2-palette__grid">
                <li v-for="item in group.items" :key="`${item.entry.kind}:${item.entry.key}`" class="pb2-palette__cell">
                    <button
                        type="button"
                        class="pb2-palette__tile"
                        :class="{ 'pb2-palette__tile--dimmed': !insertable(item) }"
                        :title="insertable(item) ? item.label : t('columns_needs_section')"
                        :dusk="`pb2-tile-${item.entry.key}`"
                        @click="insert(item)"
                        @pointerdown="drag($event, item)"
                    >
                        <span class="pb2-palette__tile-icon" aria-hidden="true">
                            <load-file v-if="item.image" replace-with :src="item.image"></load-file>
                            <Icon v-else icon="phosphor/regular/squares-four" class="size-5" />
                        </span>
                        <span class="pb2-palette__tile-label">{{ item.label }}</span>
                    </button>
                    <button
                        v-tooltip="t('favorite_toggle')"
                        type="button"
                        class="pb2-palette__tile-star"
                        :class="{ 'pb2-palette__tile-star--active': history.isFavorite(item.entry) }"
                        :aria-pressed="history.isFavorite(item.entry)"
                        :aria-label="t('favorite_toggle')"
                        @click.stop="history.toggleFavorite(item.entry)"
                        @pointerdown.stop
                    >
                        <Icon icon="phosphor/regular/star" class="size-3" />
                    </button>
                </li>
            </ul>

            <!-- Oblíbené / Naposledy použité / Sdílené bloky: řádky s ikonkou -->
            <ul v-else class="pb2-palette__list">
                <li v-for="item in group.items" :key="`${item.entry.kind}:${item.entry.key}`" class="pb2-palette__row">
                    <button
                        type="button"
                        class="pb2-palette__item"
                        :class="{ 'pb2-palette__item--dimmed': !insertable(item) }"
                        :title="insertable(item) ? undefined : t('columns_needs_section')"
                        @click="insert(item)"
                        @pointerdown="drag($event, item)"
                    >
                        <span class="pb2-palette__grip" aria-hidden="true">
                            <Icon icon="phosphor/regular/dots-six-vertical" class="size-3" />
                        </span>
                        <span class="pb2-palette__row-icon" aria-hidden="true">
                            <load-file v-if="item.image" replace-with :src="item.image"></load-file>
                            <Icon v-else icon="phosphor/regular/squares-four" class="size-3.5" />
                        </span>
                        <span class="pb2-palette__label">{{ item.label }}</span>
                    </button>
                    <button
                        v-tooltip="t('favorite_toggle')"
                        type="button"
                        class="pb2-palette__star"
                        :class="{ 'pb2-palette__star--active': history.isFavorite(item.entry) }"
                        :aria-pressed="history.isFavorite(item.entry)"
                        :aria-label="t('favorite_toggle')"
                        @click.stop="history.toggleFavorite(item.entry)"
                        @pointerdown.stop
                    >
                        <Icon icon="phosphor/regular/star" class="size-3" />
                    </button>
                </li>
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import HintChip from '@src/Common/Builder/HintChip.vue';
import { useBuilderT } from '@src/Common/Builder/host';
import { rankSearch } from '@src/Common/Builder/paletteSearch';
import { mod } from '@src/Common/Builder/shortcutKeys';
import { useOneTimeHint } from '@src/Common/Builder/useOneTimeHint';
import { useBlockInsertion } from '@src/Common/Builder/useBlockInsertion';
import { usePaletteHistory } from '@src/Common/Builder/usePaletteHistory';
import type { PaletteEntry } from '@src/Common/Builder/usePaletteHistory';
import { useBuilderRegistry } from '@src/Common/Builder/useBuilderRegistry';
import { useDragMachine } from '@src/Common/Builder/useDragMachine';

const t = useBuilderT();

interface PaletteItem {
    entry: PaletteEntry;
    label: string;
    hint?: string;
    /** Type icon URL (content.asset); shared blocks have none → fallback glyph. */
    image?: string | null;
}

interface PaletteGroup {
    key: string;
    title: string;
    items: PaletteItem[];
    /** Tile grid (type catalog) vs. icon rows (favorites/recent/shared). */
    tiles?: boolean;
}

const registry = useBuilderRegistry();
const history = usePaletteHistory();
const insertion = useBlockInsertion();
const machine = useDragMachine();
const cmdkHint = useOneTimeHint('palette-cmdk');

const query = ref('');

const typeItems = computed<PaletteItem[]>(() =>
    registry.types.value.map((type) => ({
        entry: { kind: 'type', key: type.slug },
        label: type.name,
        hint: type.slug,
        image: type.image ?? null,
    })),
);

const sharedItems = computed<PaletteItem[]>(() =>
    registry.blocks.value.map((block) => ({
        entry: { kind: 'shared', key: String(block.id) },
        label: block.name,
        image: null,
    })),
);

const itemFor = (entry: PaletteEntry): PaletteItem | null => {
    const pool = entry.kind === 'type' ? typeItems.value : sharedItems.value;

    return pool.find((item) => item.entry.key === entry.key) ?? null;
};

const groups = computed<PaletteGroup[]>(() => {
    const trimmed = query.value.trim();

    if (trimmed !== '') {
        return [
            {
                key: 'types',
                title: t('catalog_types'),
                items: rankSearch(trimmed, typeItems.value),
                tiles: true,
            },
            {
                key: 'shared',
                title: t('catalog_shared'),
                items: rankSearch(trimmed, sharedItems.value),
            },
        ].filter((group) => group.items.length > 0);
    }

    const resolve = (entries: PaletteEntry[]): PaletteItem[] =>
        entries.map(itemFor).filter((item): item is PaletteItem => item !== null);

    return [
        { key: 'favorites', title: t('favorites'), items: resolve(history.favorites.value) },
        { key: 'recent', title: t('recent'), items: resolve(history.recent.value) },
        { key: 'types', title: t('catalog_types'), items: typeItems.value, tiles: true },
        { key: 'shared', title: t('catalog_shared'), items: sharedItems.value },
    ].filter((group) => group.items.length > 0);
});

const insertable = (item: PaletteItem): boolean =>
    item.entry.kind !== 'type' || insertion.canInsertType(item.entry.key);

const insert = (item: PaletteItem): void => {
    if (item.entry.kind === 'shared') {
        insertion.insertSharedBlock(item.entry.key);
        return;
    }

    insertion.insertTypeBlock(item.entry.key);
};

const drag = (event: PointerEvent, item: PaletteItem): void => {
    if (item.entry.kind === 'shared') {
        machine.beginPointerDrag(event, { kind: 'new-shared', blockId: item.entry.key }, item.label);
        return;
    }

    machine.beginPointerDrag(event, { kind: 'new', typeSlug: item.entry.key }, item.label);
};
</script>

<style scoped>
.pb2-palette {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.pb2-palette__search-wrap {
    position: relative;
    display: flex;
}

.pb2-palette__search {
    width: 100%;
    height: 24px;
    padding: 0 2.25rem 0 0.5rem;
    font-size: 0.71875rem;
    color: var(--pb2-text, #3f3f46);
    background: var(--pb2-input-bg, var(--pb2-surface, #fafafa));
    border: 1px solid color-mix(in srgb, var(--pb2-border, #e4e4e7) 55%, transparent);
    border-radius: 4px;
}

/* rc2: keyboard-shortcut hint chip on the right of the block search. */
.pb2-palette__search-kbd {
    position: absolute;
    top: 50%;
    right: 0.3125rem;
    transform: translateY(-50%);
    padding: 1px 4px;
    font-family: var(--font-sans, inherit);
    font-size: 0.5625rem;
    line-height: 1.3;
    color: color-mix(in srgb, var(--pb2-muted, #a1a1aa) 80%, transparent);
    background: color-mix(in srgb, var(--pb2-border, #e4e4e7) 40%, transparent);
    border: 1px solid color-mix(in srgb, var(--pb2-border, #e4e4e7) 55%, transparent);
    border-radius: 3px;
    pointer-events: none;
}

.pb2-palette__search::placeholder {
    color: color-mix(in srgb, var(--pb2-muted, #a1a1aa) 65%, transparent);
}

.pb2-palette__search:focus {
    outline: none;
    border-color: var(--pb2-accent, #2563eb);
}

.pb2-palette__empty {
    margin: 0;
    font-size: 0.75rem;
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-palette__group-title {
    margin: 0.75rem 0.25rem 0.25rem;
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: color-mix(in srgb, var(--pb2-muted, #a1a1aa) 65%, transparent);
}

.pb2-palette__group:first-of-type .pb2-palette__group-title {
    margin-top: 0.25rem;
}

/* ============ dlaždicová mřížka typů ============ */

.pb2-palette__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.3125rem;
    margin: 0;
    padding: 0;
    list-style: none;
}

.pb2-palette__cell {
    position: relative;
}

.pb2-palette__tile {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    width: 100%;
    min-height: 56px;
    padding: 0.5rem 0.25rem 0.375rem;
    color: var(--pb2-muted, #a1a1aa);
    background: none;
    border: 1px solid color-mix(in srgb, var(--pb2-border, #e4e4e7) 50%, transparent);
    border-radius: 4px;
    cursor: pointer;
    touch-action: none;
    transition:
        color 140ms ease,
        background-color 140ms ease;
}

.pb2-palette__tile:hover {
    color: var(--pb2-text, #3f3f46);
    background: rgb(127 127 127 / 0.045);
}

.pb2-palette__tile:active {
    transform: translateY(0.5px);
}

.pb2-palette__tile:focus-visible {
    outline: 2px solid var(--pb2-accent, #2563eb);
    outline-offset: 1px;
}

.pb2-palette__tile--dimmed {
    opacity: 0.4;
}

/* pevný box — core ikony (currentColor) se tintují dědičností barvy,
   barevné theme ikony se jen vejdou; svg žije v shadow DOM <load-file>,
   styluje se HOST element (vzor v1 .builder-palette__item-icon) */
.pb2-palette__tile-icon {
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    overflow: hidden;
}

.pb2-palette__tile-icon load-file {
    display: inline-flex;
    width: 20px;
    height: 20px;
}

.pb2-palette__tile-icon svg {
    width: 100%;
    height: 100%;
}

.pb2-palette__tile-label {
    max-width: 100%;
    font-size: 0.625rem;
    line-height: 1.25;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    /* Block names are the primary affordance of the picker — keep them at full
     * text contrast rather than the muted tile color (which reads near-black on
     * dark surfaces). The icon stays muted; hover still lifts the whole tile. */
    color: var(--pb2-text, #3f3f46);
}

.pb2-palette__tile-star {
    position: absolute;
    top: 2px;
    right: 2px;
    display: grid;
    place-items: center;
    width: 18px;
    height: 18px;
    color: var(--pb2-muted, #a1a1aa);
    background: none;
    border: 0;
    cursor: pointer;
    opacity: 0;
    transition:
        opacity 140ms ease,
        color 140ms ease;
}

.pb2-palette__cell:hover .pb2-palette__tile-star,
.pb2-palette__tile-star:focus-visible,
.pb2-palette__tile-star--active {
    opacity: 1;
}

.pb2-palette__tile-star:hover,
.pb2-palette__tile-star--active {
    color: var(--pb2-text, #3f3f46);
}

@media (prefers-reduced-motion: reduce) {
    .pb2-palette__tile,
    .pb2-palette__tile-star,
    .pb2-palette__star,
    .pb2-palette__item,
    .pb2-palette__grip {
        transition: none;
    }
}

/* ============ řádky (oblíbené / recent / sdílené) ============ */

.pb2-palette__list {
    margin: 0;
    padding: 0;
    list-style: none;
}

.pb2-palette__row {
    display: flex;
    align-items: center;
    border-radius: 4px;
}

.pb2-palette__row:hover {
    background: rgb(127 127 127 / 0.045);
}

.pb2-palette__item {
    display: flex;
    align-items: center;
    gap: 0.4375rem;
    flex: 1;
    min-width: 0;
    height: 24px;
    padding: 0 0.3125rem;
    font-size: 0.71875rem;
    text-align: left;
    color: var(--pb2-muted, #a1a1aa);
    background: none;
    border: 0;
    border-radius: 4px;
    cursor: grab;
    touch-action: none;
    transition: color 140ms ease;
}

.pb2-palette__row:hover .pb2-palette__item,
.pb2-palette__item:focus-visible {
    color: var(--pb2-text, #3f3f46);
}

.pb2-palette__grip {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 12px;
    color: color-mix(in srgb, var(--pb2-muted, #a1a1aa) 65%, transparent);
    opacity: 0;
    transition: opacity 140ms ease;
}

.pb2-palette__row:hover .pb2-palette__grip,
.pb2-palette__item:focus-visible .pb2-palette__grip {
    opacity: 1;
}

.pb2-palette__item--dimmed {
    opacity: 0.4;
}

.pb2-palette__row-icon {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    overflow: hidden;
    color: var(--pb2-muted, #a1a1aa);
}

.pb2-palette__row-icon load-file {
    display: inline-flex;
    width: 16px;
    height: 16px;
}

.pb2-palette__row-icon svg {
    width: 100%;
    height: 100%;
}

.pb2-palette__label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.pb2-palette__star {
    flex-shrink: 0;
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    color: var(--pb2-muted, #a1a1aa);
    background: none;
    border: 0;
    cursor: pointer;
    opacity: 0;
    transition:
        opacity 140ms ease,
        color 140ms ease;
}

.pb2-palette__row:hover .pb2-palette__star,
.pb2-palette__star:focus-visible,
.pb2-palette__star--active {
    opacity: 1;
}

.pb2-palette__star:hover,
.pb2-palette__star--active {
    color: var(--pb2-text, #3f3f46);
}
</style>
